import React, { useId, useState } from "react";
import { Button, Dialog, Field, Input, QtyStepper, SegmentedControl } from "../../components/ui";
import { CatalogList } from "../../components/patterns/CatalogList";
import { plural, t } from "../../i18n";
import { ptBR } from "../../i18n/pt-BR";
import { KitKind, normalizeKitName, useKitBuilder } from "./useKitBuilder";

interface KitBuilderProps {
  /** Tipo aberto primeiro: o da tela que chamou. */
  kind: KitKind;
  onClose: () => void;
  onCreated: (kind: KitKind, name: string) => void;
}

// "Novo pacote personalizado": nome, tipo (procedimentos ou OPME) e o catálogo com a quantidade de cada item.
export const KitBuilder: React.FC<KitBuilderProps> = ({ kind: initialKind, onClose, onCreated }) => {
  const { catalogs, selection, setQuantity, create, saving } = useKitBuilder(onCreated);
  const [kind, setKind] = useState(initialKind);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const formId = useId();

  const finalName = normalizeKitName(name);
  const chosen = selection[kind];
  const itemCount = Object.keys(chosen).length;
  const unitCount = Object.values(chosen).reduce((a, b) => a + b, 0);
  const isOpme = kind === "opme";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalName) return setNameError(true);
    if (itemCount) create(kind, name);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      closeDisabled={saving}
      title={t("kitBuilder.title")}
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-3">
          <p className="text-caption text-fg-muted" aria-live="polite">
            {itemCount
              ? t("kitBuilder.selected", { items: plural(itemCount, ptBR.kitBuilder.items), units: plural(unitCount, ptBR.kitBuilder.units) })
              : t("kitBuilder.noneSelected")}
          </p>
          <div className="flex gap-2.5">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" className="flex-1" type="submit" form={formId} loading={saving} disabled={!itemCount}>
              {t("kitBuilder.create")}
            </Button>
          </div>
        </div>
      }
    >
      {/* text-left: o Dialog pode centralizar o texto */}
      <div className="flex flex-col gap-3 text-left">
        {/* Só o nome fica no form: Enter na busca adiciona item, não cria o pacote. */}
        <form id={formId} onSubmit={submit} noValidate>
          <Field
            label={t("kitBuilder.name")}
            error={nameError ? t("kitBuilder.nameMissing") : undefined}
            hint={finalName && finalName !== name.trim() ? t("kitBuilder.savedAs", { name: finalName }) : undefined}
          >
            <Input
              value={name}
              maxLength={120}
              placeholder={t("kitBuilder.namePlaceholder")}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(false);
              }}
            />
          </Field>
        </form>

        <SegmentedControl
          aria-label={t("kitBuilder.kind")}
          tone="soft"
          value={kind}
          onChange={setKind}
          options={[
            { value: "proc", label: t("kitBuilder.procedures"), count: Object.keys(selection.proc).length || undefined },
            { value: "opme", label: t("kitBuilder.opme"), count: Object.keys(selection.opme).length || undefined },
          ]}
        />

        <CatalogList
          // remonta ao trocar o tipo para limpar a busca
          key={kind}
          items={catalogs[kind]?.map((c) => ({ id: String(c.id), code: c.code, label: c.name })) ?? null}
          addedIds={new Set(Object.keys(chosen))}
          onAdd={(entry) => setQuantity(kind, Number(entry.id), 1)}
          renderAdded={(entry) => (
            <QtyStepper
              value={chosen[Number(entry.id)]}
              onChange={(q) => setQuantity(kind, Number(entry.id), q)}
              onRemove={() => setQuantity(kind, Number(entry.id), 0)}
              label={entry.label}
            />
          )}
          searchLabel={t("kitBuilder.search")}
          placeholder={t(isOpme ? "kitBuilder.searchOpme" : "kitBuilder.searchProc")}
          empty={() => t("kitBuilder.notFound")}
        />
      </div>
    </Dialog>
  );
};
